import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import API from '../endpoints';
import { Match, MatchPeriodFilter, MatchSort, MatchStatusFilter, PaginatedResponse } from '@repo/types';
import { getAccessToken } from '@data/utils/getAccessToken';
import { useGlobalContext } from '@slc/tools/GlobalContext';

// 60 seconds frontend cache for fetching matches
const CACHE_TIME = 60 * 1000;

/** The only value the API accepts for the cross-league opt-in. */
export type CrossLeagueOptIn = 'true';

export type MatchesQueryParams = {
  withLiveScores?: boolean;
  page?: number;
  limit?: number;
  period?: MatchPeriodFilter;
  startDate?: string;
  endDate?: string;
  sort?: MatchSort;
  status?: MatchStatusFilter | string;
  teamId?: string | null;
  homeTeamId?: string | null;
  awayTeamId?: string | null;
  competition?: string | null;
  /**
   * `'true'` serves the competitions named in `competition` in full, instead of scoping them to the
   * tenant's own clubs and divisions. Opt-in: without it a national cup returns only the tenant's
   * own ties. Requires `competition` — the API ignores the flag on its own.
   *
   * Typed as the literal rather than `string` because the API compares it for exact equality:
   * `'True'`, `'1'` and a typo would all type-check as `string` and silently mean "stay scoped",
   * which surfaces as missing fixtures rather than a compile error.
   */
  includeAllCompetitionClubs?: CrossLeagueOptIn;
  periodLimit?: number;
  /** '1' to include each match's live-feed events in the response. */
  withEvents?: string;
  // Scopes results to a specific season config (mirrors useLeagueTableQuery's seasonId).
  seasonId?: string;
  /**
   * Scopes results to a season by NAME (e.g. '2026/2027'), resolved server-side to the date envelope
   * across the tenant's configs for it and INTERSECTED with any date range — so a season and a date
   * narrow each other rather than one winning. `'all'` explicitly means no season constraint, and an
   * unknown name is treated the same way rather than erroring. Wins over `seasonId` when both are set.
   */
  season?: string;
  /**
   * Round label, matched exactly by the API ("Match 10" for an IFAS league round). Needs exactly one `competition`;
   * with no other date scope the API bounds it to that competition's current season.
   */
  round?: string;
};

type QueryOptions = {
  enabled?: boolean;
  refetchInterval?: number | false;
  refetchOnWindowFocus?: boolean;
};

export const useMatchesQuery = (params: MatchesQueryParams, options?: QueryOptions) => {
  const { apiUrl } = useGlobalContext();
  const accessToken = getAccessToken();

  return useQuery({
    queryKey: ['matches', params, apiUrl],
    queryFn: () => fetchMatches(apiUrl, params),
    staleTime: !accessToken ? CACHE_TIME : undefined,
    enabled: !!apiUrl && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
  });
};

const fetchMatches = async (apiUrl: string, params: MatchesQueryParams): Promise<PaginatedResponse<Match>> => {
  const accessToken = getAccessToken();

  let headers = {};
  if (accessToken) {
    headers = {
      Authorization: `Bearer ${accessToken}`,
    };
  }

  const { data } = await axios.get<PaginatedResponse<Match>>(`${apiUrl}${API.endpoints.matches}`, {
    params,
    headers,
  });

  return data;
};
